import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabase/supabase';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // 1. Extract tokens from the URL
        const hash = window.location.hash;
        const search = window.location.search;
        const fragment = hash.includes('access_token') ? hash.substring(hash.indexOf('access_token')) : '';
        const params = new URLSearchParams(fragment || search.slice(1));
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        // 2. If tokens exist in URL, manually set the session
        if (accessToken) {
          const { error: setError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || accessToken,
          });
          if (setError) throw setError;
        }

        // 3. Get the now-restored session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw new Error('Authentication session could not be established. Please try logging in again.');
        }

        const user = session.user;
        const provider = user.app_metadata?.provider || 'email';
        const isOAuthSignIn = provider !== 'email';

        // SPECIAL ADMIN OVERRIDE: If this is the primary admin email, ensure the role is set
        if (user.email === 'admin@admin.com') {
          console.log("Admin detected, verifying rights...");
          await supabase.rpc('claim_admin_rights');
        }

        // 4. Fetch/Sync Profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles_1740423033529')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        // Apply pending role only for OAuth signups
        const pendingRole = localStorage.getItem('pending_role');
        if (pendingRole && profile.role !== pendingRole && !profile.onboarding_completed) {
          await supabase.from('profiles_1740423033529').update({ role: pendingRole }).eq('id', user.id);
          profile.role = pendingRole;
        }
        localStorage.removeItem('pending_role');

        // 5. Direct Navigation based on role and status
        if (profile.role === 'admin' || user.email === 'admin@admin.com') {
          navigate('/admin/dashboard');
        } else if (profile.role === 'barber') {
          navigate(profile.onboarding_completed ? '/barber/dashboard' : '/barber/onboarding');
        } else {
          // CUSTOMERS
          if (isOAuthSignIn) {
            const needsPhone = !profile.phone || profile.phone.trim().length < 5;
            if (needsPhone && !profile.onboarding_completed) {
              navigate('/app/onboarding');
              return;
            }
          }
          navigate('/app');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-brand-light flex items-center justify-center p-6 text-center">
      {error ? (
        <div className="bg-red-50 text-red-600 p-8 rounded-3xl border border-red-100 shadow-xl max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-xl font-bold">!</span>
          </div>
          <h2 className="text-xl font-bold mb-2">Sign In Failed</h2>
          <p className="text-sm mb-4">{error}</p>
          <button onClick={() => navigate('/login')} className="text-xs font-black uppercase tracking-widest text-brand-primary hover:underline">
            Return to Login
          </button>
        </div>
      ) : (
        <div className="animate-in fade-in duration-700">
          <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mb-8 mx-auto" />
          <h1 className="text-2xl font-extrabold text-brand-dark tracking-tight">Syncing your account...</h1>
          <p className="text-gray-400 font-bold uppercase text-[10px] tracking-[0.2em] mt-4">Security Handshake in Progress</p>
        </div>
      )}
    </div>
  );
}